import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import { addDays, addWeeks, addMonths, endOfYear, endOfMonth, getDay, setDay, isWeekend } from 'date-fns';

/**
 * Get Date Picker Format
 *
 * Release plus forces us to use a weird date format
 * (20190420)
 * to format it for the date picker I need to add '/'
 * and create a date object
 *
 * @param {String} date - ie (20190420)
 * @return {Date} formattedDate
 */
export var getDatePickerFormat = function getDatePickerFormat(date) {
  return new Date(date.value.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3'));
};

/**
 * Get Plus Release Date Format
 *
 * Release plus forces us to use a weird date format
 * (20190420)
 * To get this date. I take the date picker date
 * get the day, month, year and string them together
 *
 * @param {Date} date - Date object
 * @return {string} formattedDate ie (20190420)
 */
export var getPlusReleaseDateFormat = function getPlusReleaseDateFormat(date) {
  // add 0 if less than 10
  //ie 9 => 09, 12 => 12
  var pad = function pad(n) {
    return n < 10 ? '0' + n : n;
  };
  if (date === null) return null;
  var dateDay = pad(date.getDate()).toString();
  var dateMonth = pad(date.getMonth() + 1).toString();
  var dateYear = date.getFullYear().toString();
  return dateYear + dateMonth + dateDay;
};

/**
 * Get Min Date
 *
 * Return date three days in future
 *
 * @return {Date} min date for date picker
 */
export var getMinDate = function getMinDate(date) {
  return addDays(date, 10);
};
/**
 * Get Max Date
 *
 * This function takes current day and gets end of year date
 * as well as three months away date
 * which ever date is further away, it will return that as end date
 *
 * @param {Date} date - Date object
 * @return {Date} end date for date picker
 */
export var getMaxDate = function getMaxDate(date) {
  var endOfYearDate = endOfYear(date);
  var threeMonthsAway = endOfMonth(addMonths(date, 3));
  var maxDate = endOfYearDate > threeMonthsAway ? endOfYearDate : threeMonthsAway;
  return maxDate;
};

/**
 * Get Excluded Dates
 *
 * this function takes in the datepicker dates
 * and filters disables all weekends and blackout dates
 * determined by dormify
 *
 * @param {Date} date - Date object
 * @return {Date} date if not weekend or blackout date
 */
export var getExcludedDates = function getExcludedDates(date) {
  var thisYear = new Date().getFullYear();
  var nextYear = thisYear + 1;

  var holidayDates = [].concat.apply([], [getSolidHolidayDates(thisYear), getSolidHolidayDates(nextYear), getFloatingHolidayDates(thisYear), getFloatingHolidayDates(nextYear)]);
  // flatten array
  return isWeekend(date) || holidayDates.includes(date.getTime());
};

/**
 * Calc Date from Day
 *
 * start with year and month to get the holiday month
 * find the first day of the week you are looking for 1 = monday
 * add the number of weeks to get the actual date
 * ie 3rd Mon in Jan => 1/21/2019
 *
 * @param {Array} date - Date array
 * @return {Date} date - formatted date
 */
var calcDateFromDay = function calcDateFromDay(_ref) {
  var _ref2 = _slicedToArray(_ref, 4),
      year = _ref2[0],
      month = _ref2[1],
      week = _ref2[2],
      day = _ref2[3];

  var holidayMonth = new Date(year, month);
  var firstDay = setDay(holidayMonth, day, {
    weekStartsOn: getDay(holidayMonth)
  });
  var holidayDate = addWeeks(firstDay, week - 1);
  return holidayDate;
};

/**
 * Get Floating Holiday Dates
 *
 * Take array of floating holidays
 * and return the dates based on the
 * year.
 *
 * @param {Number} year
 * @return {Array} Array of dates
 */
var getFloatingHolidayDates = function getFloatingHolidayDates(year) {
  var floatingHolidays = [
  // keys are formatted as year, month,week,day
  [year, 0, 3, 1], // Martin Luther King Jr. Day (3rd Mon in Jan)
  [year, 1, 3, 1], // President's Day (3rd Mon in Feb)
  [year, 4, 5, 1], // Memorial Day (Last Mon in May)
  [year, 8, 1, 1], // Labor Day (1st Mon in Sept)
  [year, 10, 4, 4]];
  return floatingHolidays.map(function (holiday) {
    return calcDateFromDay(holiday).getTime();
  });
};

/**
 * Get Solid Holiday Dates
 *
 * Get 'Solid' holiday dates based on year
 * 12/25 => 12/25/2019
 *
 * @param {Number} year
 * @return {Array} Array of dates
 */
var getSolidHolidayDates = function getSolidHolidayDates(year) {
  return [new Date('1/1/' + year).getTime(), new Date('7/4/' + year).getTime(), new Date('12/25/' + year).getTime()];
};